import { NetworkStatus, useQuery } from '@apollo/client';
import { Box, IconSize } from '@bechtel/gds';
import { EquipmentBlockPoint, MetadataColumn, MetadataTableName } from '@bechtel/sr-types';
import { GridApi } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css'; // Core grid CSS, always needed
import 'ag-grid-community/styles/ag-theme-alpine.css'; // Optional theme CSS
import { ColDef } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { cloneDeep, find, forEach } from 'lodash';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { useAppUser, useLanguage } from '~/components';
import { AppSpinner } from '~/components/atoms';
import { DEFAULT_FONT_SIZE, DEFAULT_ROW_HEIGHT } from '~/components/organisms/grid/types';
import { GET_BLOCK_POINTS_BY_EQUIPMENTS } from '~/gql';
import { useTableMetadataQuery } from '~/hooks';
import { NoData } from '../no-data';
import { CustomGridWrapperStyled } from './styled';

interface EquipmentBlockPointProps {
  equipmentIds: string[];
  onGridReady: (api: GridApi, endType: string) => void;
  endType: string;
  allowRowDrag: boolean;
  rowDragManaged?: boolean;
  selectAllRows?: boolean;

  equipmemtBlockStatMap?: Map<string, { cNo: number; vNo: number }>;
}

export const EquipmentBlockPointList: FC<EquipmentBlockPointProps> = ({
  equipmentIds,
  onGridReady,
  endType,
  allowRowDrag = true,
  rowDragManaged = false,
  selectAllRows = false,
  equipmemtBlockStatMap,
}) => {
  const theme = useTheme();
  const { userAccount } = useAppUser();
  const [rowData, setRowData] = useState<EquipmentBlockPoint[]>([]);
  // const gridRef = useRef(null); // Reference to the grid
  // Get equipment block points
  const { data, loading, error, networkStatus } = useQuery<{
    blockPointsByEquipmentIds: EquipmentBlockPoint[];
  }>(GET_BLOCK_POINTS_BY_EQUIPMENTS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      equipmentIds: equipmentIds,
    },
  });
  const [getTableMetaData, { data: viewMetadata }] = useTableMetadataQuery();
  const { isAlternateLanguageUsed } = useLanguage();

  useEffect(() => {
    if (!userAccount?.defaultProject?.id) return;
    getTableMetaData({
      variables: {
        name: MetadataTableName.TERM_VIEW, //TODO: Use EQUIPMENT_BLOCK_POINT_VIEW and remove FROM; replace logic in column definitions
        projectId: userAccount?.defaultProject?.id,
      },
    });
  }, [userAccount?.defaultProject?.id, getTableMetaData]);

  const context = useMemo<any>(() => {
    return {
      name: endType,
    };
  }, [endType]);

  useEffect(() => {
    if (!data || !data.blockPointsByEquipmentIds || data.blockPointsByEquipmentIds.length === 0) {
      setRowData([]);
      return;
    }
    const blockPointData: EquipmentBlockPoint[] = cloneDeep(data.blockPointsByEquipmentIds);
    if (equipmemtBlockStatMap && equipmemtBlockStatMap?.size) {
      forEach(blockPointData, (blockPoint: EquipmentBlockPoint) => {
        const blockStat = equipmemtBlockStatMap.get(
          `${blockPoint.equipmentId}-${blockPoint.blockTag}-${blockPoint.pointTag}`
        );
        if (blockStat) {
          blockPoint.customerCount = blockStat.cNo;
          blockPoint.vendorCount = blockStat.vNo;
        }
      });
    } else {
      forEach(blockPointData, (blockPoint: EquipmentBlockPoint) => {
        equipmemtBlockStatMap?.set(`${blockPoint.equipmentId}-${blockPoint.blockTag}-${blockPoint.pointTag}`, {
          cNo: blockPoint?.customerCount || 0,
          vNo: blockPoint?.vendorCount || 0,
        });
      });
    }

    setRowData([...blockPointData]);
  }, [data, equipmemtBlockStatMap]);

  const defaultColDef = useMemo(() => {
    return {
      sortable: false,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
    };
  }, []);

  const columnDefs = useMemo<ColDef[]>(() => {
    const readOnlyCellStyle = { backgroundColor: theme.colors.grey[100] };
    const equipmentTagColumn = viewMetadata
      ? find(
          viewMetadata.tableMetadata.metadataColumns,
          (metadaColumn: MetadataColumn) => metadaColumn.memberName === 'fromEquipmentTag'
        )
      : null;
    const blockTagColumn = viewMetadata
      ? find(
          viewMetadata.tableMetadata.metadataColumns,
          (metadaColumn: MetadataColumn) => metadaColumn.memberName === 'fromBlockTag'
        )
      : null;
    const pointTagColumn = viewMetadata
      ? find(
          viewMetadata.tableMetadata.metadataColumns,
          (metadaColumn: MetadataColumn) => metadaColumn.memberName === 'fromPointTag'
        )
      : null;
    const vendorCountColumn = viewMetadata
      ? find(
          viewMetadata.tableMetadata.metadataColumns,
          (metadaColumn: MetadataColumn) => metadaColumn.memberName === 'fromVendorCount'
        )
      : null;
    const customerCountColumn = viewMetadata
      ? find(
          viewMetadata.tableMetadata.metadataColumns,
          (metadaColumn: MetadataColumn) => metadaColumn.memberName === 'fromCustomerCount'
        )
      : null;

    return [
      { field: 'id', hide: true },
      { field: 'blockId', hide: true },
      { field: 'equipmentId', hide: true },
      {
        field: 'equipmentTag',
        hide: equipmentIds && equipmentIds.length > 1 ? false : true,
        rowDrag: allowRowDrag && equipmentIds.length > 1 ? true : false,
        headerName: equipmentTagColumn
          ? isAlternateLanguageUsed
            ? equipmentTagColumn.alternateColumnHeading.replace('FROM;', '')
            : equipmentTagColumn.columnHeading.replace('FROM;', '')
          : '',
        cellStyle: readOnlyCellStyle,
      },
      {
        field: 'blockTag',
        headerName: blockTagColumn
          ? isAlternateLanguageUsed
            ? blockTagColumn.alternateColumnHeading.replace('FROM;', '')
            : blockTagColumn.columnHeading.replace('FROM;', '')
          : '',
        rowDrag: allowRowDrag && (equipmentIds.length > 1 ? false : true),
        cellStyle: readOnlyCellStyle,
      },
      {
        field: 'pointTag',
        headerName: pointTagColumn
          ? isAlternateLanguageUsed
            ? pointTagColumn.alternateColumnHeading.replace('FROM;', '')
            : pointTagColumn.columnHeading.replace('FROM;', '')
          : '',
        cellStyle: readOnlyCellStyle,
      },
      {
        field: 'vendorCount',
        headerName: vendorCountColumn
          ? isAlternateLanguageUsed
            ? vendorCountColumn.alternateColumnHeading.replace('FROM;', '')
            : vendorCountColumn.columnHeading.replace('FROM;', '')
          : '',
        cellStyle: readOnlyCellStyle,
      },
      {
        field: 'customerCount',
        headerName: customerCountColumn
          ? isAlternateLanguageUsed
            ? customerCountColumn.alternateColumnHeading.replace('FROM;', '')
            : customerCountColumn.columnHeading.replace('FROM;', '')
          : '',
        cellStyle: readOnlyCellStyle,
      },
    ];
  }, [theme, viewMetadata, equipmentIds, allowRowDrag, isAlternateLanguageUsed]);

  if (loading || networkStatus === NetworkStatus.refetch)
    return (
      <Box flexGrow={{ sm: 1 }} alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }}>
        <AppSpinner size={IconSize.SMALL}></AppSpinner>
      </Box>
    );

  return (
    <Box alignItems={{ sm: 'top' }} justifyContent={{ sm: 'center' }} flexGrow={{ sm: 1 }}>
      <CustomGridWrapperStyled fontSize={DEFAULT_FONT_SIZE}>
        <AgGridReact
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          rowData={rowData}
          animateRows={true}
          rowHeight={DEFAULT_ROW_HEIGHT}
          onGridReady={(params) => {
            onGridReady(params.api, endType);
            if (selectAllRows) params.api.selectAll();
          }}
          suppressMoveWhenRowDragging={true}
          suppressContextMenu={true}
          autoSizeStrategy={{
            type: 'fitCellContents',
          }}
          rowDragMultiRow={allowRowDrag}
          rowDragManaged={rowDragManaged}
          rowSelection="multiple"
          rowMultiSelectWithClick={true}
          context={context}
          noRowsOverlayComponent={NoData}
        ></AgGridReact>
      </CustomGridWrapperStyled>
    </Box>
  );
};

export default EquipmentBlockPointList;
